import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNull } from 'lodash/fp';

import { Colors, CustomTooltip } from './Tooltip';

interface OverflowTooltipProps {
    overlayText: JSX.Element | string | null;
    onClick?: () => void;
    arrowContent?: React.ReactNode | undefined;
    color?: keyof Colors;
    width?: number;
    trigger?: string;
    placement?: string;
    className?: string;
    testId?: string;
    isVerticalText?: boolean;
    cursor?: string;
}

export const OverflowTooltip: React.FC<OverflowTooltipProps> = ({ overlayText, color, width, trigger, placement, className, testId, onClick, isVerticalText = false, cursor }) => {
    const [isOverflowed, setIsOverflow] = useState(false);
    const textElementRef = useRef<HTMLDivElement>(null);

    const compareSize = useCallback(() => {
        if (!isNull(textElementRef) && !isNull(textElementRef.current)) {
            isVerticalText ? setIsOverflow(textElementRef.current.scrollHeight > textElementRef.current.clientHeight) : setIsOverflow(textElementRef.current.scrollWidth > textElementRef.current.clientWidth);
        }
    }, [textElementRef, isVerticalText]);

    useEffect(() => {
        if (!isNull(overlayText)) {
            compareSize();
            window.addEventListener('resize', compareSize);
            () => {
                window.removeEventListener('resize', compareSize);
            };
        }
    }, [compareSize, overlayText]);

    const content = useMemo(() => isOverflowed ? overlayText : null, [isOverflowed, overlayText]);

    const defaultCursor = useMemo(() => trigger === 'click' && isOverflowed ? 'pointer' : 'default', [trigger, isOverflowed]);
    const tooltipCursor = useMemo(() => cursor || defaultCursor, [cursor, defaultCursor]);

    return (
        <CustomTooltip
            overlayText={content}
            trigger={trigger}
            color={color}
            width={width}
            placement={placement}
        >
            <div
                ref={textElementRef}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: tooltipCursor
                }}
                className={className}
                data-testid={`${testId}-overflow-tooltip`}
                onClick={onClick}
            >
                {overlayText}
            </div>
        </CustomTooltip>
    );
};
